import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Text } from '@reservamos/elements';
import PropTypes from 'prop-types';
import Loader from 'components/Loader';
import useRucValidation from 'hooks/useRucValidation';
import useBillingOperations from 'hooks/useBillingOperations';
import { trackBillingAttempt } from 'metrics/user-analytics/exchange';
import RucInput from './RucInput';
import TaxpayerInfo from './TaxpayerInfo';
import BillingSuccess from './BillingSuccess';

/**
 * BillingContent component.
 *
 * This component handles the main billing logic and orchestrates sub-components.
 *
 * @param {object} props - purchaseToken, validateChangeDocument, makeChangeDocument, selectedTicket, modalType, onClose
 * @param {string} props.purchaseToken - Token used for purchase operations
 * @param {Function} props.validateChangeDocument - Function to validate document changes
 * @param {Function} props.makeChangeDocument - Function to make document changes
 * @param {object} props.selectedTicket - The selected ticket with document information
 * @param {string} props.modalType - The type of billing modal ('invoice' or 'correction')
 * @param {Function} props.onClose - Function to close the modal
 *
 * @returns {JSX.Element} The rendered billing content component.
 */
const BillingContent = ({
  purchaseToken,
  validateChangeDocument,
  makeChangeDocument,
  selectedTicket,
  modalType,
  onClose,
}) => {
  const { t } = useTranslation('exchange');

  const [isBillingLoading, setIsBillingLoading] = useState(false);
  const [successData, setSuccessData] = useState(null);

  const {
    rucNumber,
    isValidatingRuc,
    taxpayerData,
    errorMessage,
    handleRucChange,
    handleRucValidation,
    setErrorMessage,
  } = useRucValidation(purchaseToken);

  const { validateBillingInputs, prepareBillingPayload } = useBillingOperations(
    purchaseToken,
    rucNumber,
    taxpayerData,
    setErrorMessage,
  );

  useEffect(() => {
    if (!selectedTicket) {
      setSuccessData(null);
      setIsBillingLoading(false);
    }
  }, [selectedTicket]);

  /**
   * getModalDescription Function
   *
   * Returns the appropriate description based on the modal type.
   *
   * @returns {string} The modal description
   */
  const getModalDescription = () => {
    let title = '';
    if (modalType === 'invoice') {
      title = t('modal_confirm_billing.title_change_to_invoice');
    } else if (modalType === 'correction') {
      title = t('modal_confirm_billing.title_ruc_correction');
    } else {
      title = t('modal_confirm_billing.title');
    }
    const titleLowercase = title.toLowerCase();
    return t('modal_confirm_billing.first_text_dynamic', { title: titleLowercase });
  };

  /**
   * handleBillingApiCalls Function
   *
   * Handles the API calls for billing validation and document change.
   *
   * @param {object} payload - The payload to send to the API.
   * @returns {Promise<object>} The response from the API calls.
   */
  const handleBillingApiCalls = async (payload) => {
    const validationResponse = await validateChangeDocument(payload);

    if (!validationResponse.success) {
      return {
        success: false,
        message: validationResponse.message || t('modal_confirm_billing.validation_failed'),
      };
    }

    const changeResponse = await makeChangeDocument(payload);

    if (!changeResponse.success) {
      return {
        success: false,
        message: changeResponse.message || t('modal_confirm_billing.change_document_failed'),
      };
    }

    return {
      success: true,
      data: changeResponse,
    };
  };

  /**
   * handleBillingModalConfirm Function
   *
   * Handles the confirmation of billing modal with RUC validation.
   * This function updates the legal/fiscal information for an existing purchase.
   *
   * @returns {void}
   */
  const handleBillingModalConfirm = async () => {
    if (!validateBillingInputs(selectedTicket)) {
      return;
    }

    setIsBillingLoading(true);
    setErrorMessage('');

    try {
      const payload = prepareBillingPayload(selectedTicket);

      const result = await handleBillingApiCalls(payload);

      if (result.success) {
        trackBillingAttempt(modalType, true);
        setSuccessData(result.data);
      } else {
        trackBillingAttempt(modalType, false, result.message);
        setErrorMessage(result.message);
      }
    } catch (error) {
      trackBillingAttempt(modalType, false, t('modal_confirm_billing.error'));
      setErrorMessage(t('modal_confirm_billing.error'));
    } finally {
      setIsBillingLoading(false);
    }
  };

  if (isBillingLoading || isValidatingRuc) {
    return (
      <div className="min-h-[300px] bg-white flex items-center justify-center">
        <div className="flex flex-col items-center space-y-4 p-6">
          <Loader isLoading />
          <Text size="M" className="text-center">
            {isBillingLoading
              ? t('modal_confirm_billing.loading')
              : t('modal_confirm_billing.searching_taxpayer')}
          </Text>
        </div>
      </div>
    );
  }

  if (successData) {
    return <BillingSuccess successData={successData} onClose={onClose} />;
  }

  return (
    <div className="space-y-6 relative">
      <Text>{getModalDescription()}</Text>

      <RucInput
        rucNumber={rucNumber}
        onRucChange={handleRucChange}
        onRucValidation={handleRucValidation}
        errorMessage={errorMessage}
        isDisabled={isBillingLoading}
        isValidatingRuc={isValidatingRuc}
      />

      <div
        className={`transition-all duration-500 ease-in-out transform ${
          taxpayerData ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-4 scale-95'
        }`}
      >
        <TaxpayerInfo taxpayerData={taxpayerData} />
      </div>

      <div
        className={`flex justify-between pt-4 border-t border-gray-200 transition-all duration-300 ease-in-out ${
          taxpayerData ? 'opacity-100' : 'opacity-50'
        }`}
      >
        <Button
          buttonType="button"
          text={t('modal_confirm_billing.secondary_button')}
          variant="noFill"
          isRounded
          withHeight
          onClick={onClose}
          isDisabled={isBillingLoading}
        />
        <div
          className={`transition-all duration-300 ease-in-out ${
            taxpayerData ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
          }`}
        >
          {taxpayerData && (
            <Button
              buttonType="submit"
              text={
                isBillingLoading ? (
                  <div className="flex items-center space-x-2">
                    <Loader isLoading={isBillingLoading} />
                    <span>{t('modal_confirm_billing.loading')}</span>
                  </div>
                ) : (
                  t('modal_confirm_billing.primary_button')
                )
              }
              variant="accent"
              isRounded
              withHeight
              onClick={handleBillingModalConfirm}
              isDisabled={isBillingLoading || !rucNumber.trim()}
            />
          )}
        </div>
      </div>
    </div>
  );
};

BillingContent.propTypes = {
  purchaseToken: PropTypes.string.isRequired,
  validateChangeDocument: PropTypes.func.isRequired,
  makeChangeDocument: PropTypes.func.isRequired,
  selectedTicket: PropTypes.object,
  modalType: PropTypes.string,
  onClose: PropTypes.func.isRequired,
};

export default BillingContent;
